import axios from 'axios';
import {
	BulkCreateEmployeesParamsDTO,
	BulkCreateEmployeesResponseDTO,
	CreateEmployeeResponseDTO,
	CreateEmployeesParamsDTO,
	CreateSurveyParamsDTO,
	CreateSurveyResponseDTO,
	DeleteEmployeeParamsDTO,
	DeleteEmployeeResponseDTO,
	DeleteParticipationParamsDTO,
	DeleteParticipationResponseDTO,
	EditEmployeeParamsDTO,
	EditEmployeeResponseDTO,
	GeneratePinParamsDTO,
	GeneratePinResponseDTO,
	GetActionPlansParamsDTO,
	GetActionPlansResponseDTO,
	GetAepPsychosocialParamsDTO,
	GetAepPsychosocialResponseDTO,
	GetCompletedSurveysParamsDTO,
	GetCompletedSurveysResponseDTO,
	GetEmployeeParamsDTO,
	GetEmployeeResponseDTO,
	GetEmployeesListResponseDTO,
	GetEmployeesParamsDTO,
	GetEvaluationsParamsDTO,
	GetEvaluationsResponseDTO,
	GetOneSurveyParamsDTO,
	GetOneSurveyResponseDTO,
	GetOngoingSurveysParamsDTO,
	GetOngoingSurveysResponseDTO,
	GetParticipationAnswersParamsDTO,
	GetParticipationAnswersResponseDTO,
	GetParticipationsByEmployeeParamsDTO,
	GetParticipationsByEmployeeResponseDTO,
	GetPsychosocialToolsParamsDTO,
	GetPsychosocialToolsResponseDTO,
	GetToolResultParamsDTO,
	GetToolResultResponseDTO,
	GetValidateSurveyTokenParamsDTO,
	GetValidateSurveyTokenResponseDTO,
	InitSurveyParamsDTO,
	InitSurveyResponseDTO,
	CreateSurveyAnswerParamsDTO,
	CreateSurveyAnswerResponseDTO,
	ExportPdfParamsDTO,
	ExportPdfResponseDTO,
	PatchPsychosocialSurveyResponseDTO,
	PatchPsychosocialSurveyParamsDTO,
	GetSurveysByTimeResponseDTO,
	GetSurveysByTimeParamsDTO,
	GetCompletedSurveysGeneralParamsDTO,
	GetCompletedSurveysGeneralResponseDTO,
	GetGeneralResultsByCategoryParamsDTO,
	GetGeneralResultsByCategoryResponseDTO,
	CreateGeneralPDFParamsDTO,
	CreateGeneralPDFResponseDTO,
	GetRiskMatrixConsequencesResponseDTO,
	GetRiskMatrixConsequencesParamsDTO,
	GetRiskMatrixExposuresParamsDTO,
	GetRiskMatrixExposuresResponseDTO,
	GetRiskMatrixProbabilitiesParamsDTO,
	GetRiskMatrixProbabilitiesResponseDTO,
	UpdateRiskMatrixParamsDTO,
	UpdateRiskMatrixResponseDTO,
	CreateRiskMatrixActionPlanParamsDTO,
	GetRiskMatrixBySurveyResponseDTO,
	GetRiskMatrixBySurveyParamsDTO,
	CreateRiskMatrixActionPlanResponseDTO,
	GetRiskMatrixActionPlanParamsDTO,
	GetRiskMatrixActionPlanResponseDTO,
	CreateRiskMatrixFileUrlParamsDTO,
	CreateRiskMatrixFileUrlResponseDTO,
	GetRiskMatrixAttachmentsResponseDTO,
	GetRiskMatrixAttachmentsParamsDTO,
	GetRiskMatrixActionPlanAttachmentsParamsDTO,
	GetRiskMatrixActionPlanAttachmentsResponseDTO,
	CreateRiskMatrixFileInS3ParamsDTO,
	CreateRiskMatrixAttachmentParamsDTO,
	CreateRiskMatrixAttachmentResponseDTO,
	UpdateRiskMatrixActionPlanParamsDTO,
	UpdateRiskMatrixActionPlanResponseDTO,
	DeleteRiskMatrixAttachmentParamsDTO,
	DeleteRiskMatrixAttachmentResponseDTO,
	UpdateRiskMatrixAttachmentParamsDTO,
	UpdateRiskMatrixAttachmentResponseDTO,
	GetEmployeeEducationListRequestDTO,
	CreateEmployeeEducationRequestDTO,
	CreateEmployeeEducationResponseDTO,
	GetEmployeeWorkShiftListRequestDTO,
	CreateEmployeeWorkShiftRequestDTO,
	CreateEmployeeWorkShiftResponseDTO,
	CreateEmployeeSurveyResultRequestDTO,
	CreateEmployeeSurveyResultResponseDTO,
} from '@/core/dto/psychosocial-report';
import {
	BulkCreateEmployeeMapper,
	CreateEmployeeMapper,
	CreateSurveyMapper,
	DeleteEmployeeMapper,
	EditEmployeeMapper,
	GeneratePinMapper,
	GetActionPlansMapper,
	GetCompletedSurveysMapper,
	GetEmployeeMapper,
	GetEmployeesListMapper,
	GetEvaluationsMapper,
	GetOneSurveyMapper,
	GetOngoingSurveysMapper,
	GetToolResultMapper,
	GetToolsMapper,
	GetValidateSurveyTokenMapper,
	InitSurveyMapper,
	CreateSurveyAnswerMapper,
	GetParticipationAnswersMapper,
	GetAepPsychosocialMapper,
	GetParticipationsByEmployeeMapper,
	PatchSurveyMapper,
	GetSurveysByTimeMapper,
	GetCompletedSurveysGeneralMapper,
	GetGeneralResultsByCategoryMapper,
} from '@/core/mappers/psychosocial-report';
import { BaseService } from './base-service';
import type {
	Employee,
	EmployeeList,
	GetToolsResponse,
	CreateSurveyResponse,
	EditEmployeeResponse,
	GetOneSurveyResponse,
	DeleteEmployeeResponse,
	CreateEmployeeResponse,
	GetOngoingSurveysResponse,
	BulkCreateEmployeesResponse,
	GetCompletedSurveysResponse,
	GetValidateSurveyTokenResponse,
	GeneratePinResponse,
	GetToolResultResponse,
	GetEvaluationsResponse,
	InitSurveyResponse,
	GetAepPsychosocialResponse,
	GetActionPlansResponse,
	CreateSurveyAnswerResponse,
	GetParticipationAnswersResponse,
	GetParticipationsByEmployeeResponse,
	PatchPsychosocialSurveyResponse,
	GetSurveysByTimeResponse,
	GetCompletedSurveysGeneralResponse,
	GetGeneralResultsByCategoryResponse,
	EmployeeEducation,
	EmployeeWorkShift,
} from '@/types/Psychosocial';

export class PsychosocialReportService extends BaseService {
	private static instance: PsychosocialReportService;

	private constructor(public readonly basePath: string = '/psychosocial_report') {
		super();
	}

	public static getInstance(): PsychosocialReportService {
		if (!PsychosocialReportService.instance) {
			PsychosocialReportService.instance = new PsychosocialReportService();
		}
		return PsychosocialReportService.instance;
	}

	public async getEmployeesList(params: GetEmployeesParamsDTO): Promise<EmployeeList> {
		const url = this.basePath + '/employees';
		const { data } = await this.getInstance().get<GetEmployeesListResponseDTO>(url, { params });
		return new GetEmployeesListMapper(data).toDomain();
	}

	public async getEmployee(params: GetEmployeeParamsDTO): Promise<Employee> {
		const { employee_id, organization_id, company_id } = params;
		const url = this.basePath + `/employee/${employee_id}`;
		const { data } = await this.getInstance().get<GetEmployeeResponseDTO>(url, {
			params: { organization_id, company_id },
		});
		return new GetEmployeeMapper(data).toDomain();
	}

	public async deleteEmployee(params: DeleteEmployeeParamsDTO): Promise<DeleteEmployeeResponse> {
		const { id, organization_id, company_id } = params;
		const { data } = await this.getInstance().delete<DeleteEmployeeResponseDTO>(this.basePath + `/employee/${id}`, {
			params: { organization_id, company_id },
		});
		return new DeleteEmployeeMapper(data).toDomain();
	}

	public async createEmployee(params: CreateEmployeesParamsDTO): Promise<CreateEmployeeResponse> {
		const url = this.basePath + '/employee/import';
		const { data } = await this.getInstance().post<CreateEmployeeResponseDTO>(url, params);
		return new CreateEmployeeMapper(data).toDomain();
	}

	public async bulkCreateEmployees(params: BulkCreateEmployeesParamsDTO): Promise<BulkCreateEmployeesResponse> {
		const url = this.basePath + '/employee/create/bulk';
		const { data } = await this.getInstance().post<BulkCreateEmployeesResponseDTO>(url, params);
		return new BulkCreateEmployeeMapper(data).toDomain();
	}

	public async editEmployee(params: EditEmployeeParamsDTO): Promise<EditEmployeeResponse> {
		const url = this.basePath + `/employee/${params.employee_id}`;
		const { data } = await this.getInstance().put<EditEmployeeResponseDTO>(url, params);
		return new EditEmployeeMapper(data).toDomain();
	}

	public async createSurvey(params: CreateSurveyParamsDTO): Promise<CreateSurveyResponse> {
		const url = this.basePath + `/survey`;
		const { data } = await this.getInstance().post<CreateSurveyResponseDTO>(url, params);
		return new CreateSurveyMapper(data).toDomain();
	}

	public async updateSurvey(params: PatchPsychosocialSurveyParamsDTO): Promise<PatchPsychosocialSurveyResponse> {
		const url = this.basePath + `/survey/${params.survey_id}`;
		const { data } = await this.getInstance().patch<PatchPsychosocialSurveyResponseDTO>(url, params);
		return new PatchSurveyMapper(data).toDomain();
	}

	public async getTools(params: GetPsychosocialToolsParamsDTO): Promise<GetToolsResponse> {
		const url = this.basePath + `/tools`;
		const { data } = await this.getInstance().get<GetPsychosocialToolsResponseDTO>(url, {
			params,
		});
		return new GetToolsMapper(data).toDomain();
	}

	public async getOngoingSurveysList(params: GetOngoingSurveysParamsDTO): Promise<GetOngoingSurveysResponse> {
		const url = this.basePath + `/surveys/ongoing`;
		const { data } = await this.getInstance().get<GetOngoingSurveysResponseDTO>(url, { params });
		return new GetOngoingSurveysMapper(data).toDomain();
	}

	public async getCompletedSurveysList(params: GetCompletedSurveysParamsDTO): Promise<GetCompletedSurveysResponse> {
		const url = this.basePath + `/surveys/completed`;
		const { data } = await this.getInstance().get<GetCompletedSurveysResponseDTO>(url, { params });
		return new GetCompletedSurveysMapper(data).toDomain();
	}

	public async getOneSurvey(params: GetOneSurveyParamsDTO): Promise<GetOneSurveyResponse> {
		const url = this.basePath + `/surveys/${params.survey_id}`;
		const { data } = await this.getInstance().get<GetOneSurveyResponseDTO>(url, { params });
		return new GetOneSurveyMapper(data).toDomain();
	}

	public async deleteParticipation(params: DeleteParticipationParamsDTO): Promise<DeleteParticipationResponseDTO> {
		const url = this.basePath + `/surveys/participation/${params.participation_id}`;
		const { data } = await this.getInstance().delete<DeleteParticipationResponseDTO>(url, { params });
		return data;
	}

	public async getValidateSurveyToken(
		params: GetValidateSurveyTokenParamsDTO,
	): Promise<GetValidateSurveyTokenResponse> {
		const url = `/public/psychosocial/survey/${params.token_survey}`;
		const { data } = await this.getInstance().get<GetValidateSurveyTokenResponseDTO>(url);
		return new GetValidateSurveyTokenMapper(data).toDomain();
	}

	public async generatePin(params: GeneratePinParamsDTO): Promise<GeneratePinResponse> {
		const url = `/public/psychosocial/survey/pin`;
		const { data } = await this.getInstance().post<GeneratePinResponseDTO>(url, params);
		return new GeneratePinMapper(data).toDomain();
	}

	public async initSurvey(params: InitSurveyParamsDTO): Promise<InitSurveyResponse> {
		const url = `/public/psychosocial/survey/init`;
		const { data } = await this.getInstance().post<InitSurveyResponseDTO>(url, params);
		return new InitSurveyMapper(data).toDomain();
	}

	public async getToolResult(params: GetToolResultParamsDTO): Promise<GetToolResultResponse> {
		const url = this.basePath + `/result_per_category/${params.survey_id}`;
		const { data } = await this.getInstance().get<GetToolResultResponseDTO>(url, { params });
		return new GetToolResultMapper(data).toDomain();
	}

	public async getEvaluations(params: GetEvaluationsParamsDTO): Promise<GetEvaluationsResponse> {
		const url = this.basePath + `/result_avaliations/${params.survey_id}`;
		const { data } = await this.getInstance().get<GetEvaluationsResponseDTO>(url, { params });
		return new GetEvaluationsMapper(data).toDomain();
	}

	public async getAepPsychosocial(params: GetAepPsychosocialParamsDTO): Promise<GetAepPsychosocialResponse> {
		const url = this.basePath + `/aep/${params.survey_id}`;
		const { data } = await this.getInstance().get<GetAepPsychosocialResponseDTO>(url, { params });
		return new GetAepPsychosocialMapper(data).toDomain();
	}

	public async getActionPlans(params: GetActionPlansParamsDTO): Promise<GetActionPlansResponse> {
		const url = this.basePath + `/action_plans/${params.survey_id}`;
		const { data } = await this.getInstance().get<GetActionPlansResponseDTO>(url, { params });
		return new GetActionPlansMapper(data).toDomain();
	}

	public async createSurveyAnswer(params: CreateSurveyAnswerParamsDTO): Promise<CreateSurveyAnswerResponse> {
		const url = `/public/psychosocial/survey/answer`;
		const { data } = await this.getInstance().post<CreateSurveyAnswerResponseDTO>(url, params);
		return new CreateSurveyAnswerMapper(data).toDomain();
	}

	public async createEmployeeSurveyResult(
		params: CreateEmployeeSurveyResultRequestDTO,
	): Promise<CreateEmployeeSurveyResultResponseDTO> {
		const url = `/public/psychosocial/survey/result`;
		const { data } = await this.getInstance().post<CreateEmployeeSurveyResultResponseDTO>(url, params);
		return data;
	}

	public async getParticipationAnswers(
		params: GetParticipationAnswersParamsDTO,
	): Promise<GetParticipationAnswersResponse> {
		const url = `/public/psychosocial/survey/participation/${params.participation_id}`;
		const { data } = await this.getInstance().get<GetParticipationAnswersResponseDTO>(url, { params });
		return new GetParticipationAnswersMapper(data).toDomain();
	}

	public async getParticipationsByEmployee(
		params: GetParticipationsByEmployeeParamsDTO,
	): Promise<GetParticipationsByEmployeeResponse> {
		const url = `/public/psychosocial/survey/participations/employee/${params.employee_id}`;
		const { data } = await this.getInstance().get<GetParticipationsByEmployeeResponseDTO>(url);
		return new GetParticipationsByEmployeeMapper(data).toDomain();
	}

	public async exportPdf(params: ExportPdfParamsDTO): Promise<ExportPdfResponseDTO> {
		const url = this.basePath + `/psychosocial_pdf/${params.survey_id}`;
		const { data } = await this.getInstance().get<ExportPdfResponseDTO>(url, { params });
		return data;
	}

	public async getSurveysByTime(params: GetSurveysByTimeParamsDTO): Promise<GetSurveysByTimeResponse> {
		const url = this.basePath + `/surveys_on_time`;
		const { data } = await this.getInstance().get<GetSurveysByTimeResponseDTO>(url, { params });
		return new GetSurveysByTimeMapper(data).toDomain();
	}

	public async getCompletedSurveysGeneral(
		params: GetCompletedSurveysGeneralParamsDTO,
	): Promise<GetCompletedSurveysGeneralResponse> {
		const url = this.basePath + `/completed_surveys_general`;
		const { data } = await this.getInstance().get<GetCompletedSurveysGeneralResponseDTO>(url, { params });
		return new GetCompletedSurveysGeneralMapper(data).toDomain();
	}

	public async getGeneralResultsByCategory(
		params: GetGeneralResultsByCategoryParamsDTO,
	): Promise<GetGeneralResultsByCategoryResponse> {
		const url = this.basePath + `/general_by_category`;
		const { data } = await this.getInstance().get<GetGeneralResultsByCategoryResponseDTO>(url, { params });
		return new GetGeneralResultsByCategoryMapper(data).toDomain();
	}

	public async createGeneralPDF(params: CreateGeneralPDFParamsDTO): Promise<CreateGeneralPDFResponseDTO> {
		const url = this.basePath + `/psychosocial_general_pdf`;
		const { data } = await this.getInstance().post<CreateGeneralPDFResponseDTO>(url, params);
		return data;
	}

	public async getRiskMatrixConsequences(
		params: GetRiskMatrixConsequencesParamsDTO,
	): Promise<GetRiskMatrixConsequencesResponseDTO[]> {
		const url = this.basePath + `/risk_matrix/consequences`;
		const { data } = await this.getInstance().get(url, { params });
		return data;
	}

	public async getRiskMatrixExposures(
		params: GetRiskMatrixExposuresParamsDTO,
	): Promise<GetRiskMatrixExposuresResponseDTO[]> {
		const url = this.basePath + `/risk_matrix/exposures`;
		const { data } = await this.getInstance().get(url, { params });
		return data;
	}

	public async getRiskMatrixProbabilities(
		params: GetRiskMatrixProbabilitiesParamsDTO,
	): Promise<GetRiskMatrixProbabilitiesResponseDTO[]> {
		const url = this.basePath + `/risk_matrix/probabilities`;
		const { data } = await this.getInstance().get(url, { params });
		return data;
	}

	public async getRiskMatrixBySurvey(
		params: GetRiskMatrixBySurveyParamsDTO,
	): Promise<GetRiskMatrixBySurveyResponseDTO[]> {
		const url = this.basePath + `/risk_matrix/surveys/${params.survey_id}`;
		const { data } = await this.getInstance().get(url, { params });
		return data;
	}

	public async updateRiskMatrix(params: UpdateRiskMatrixParamsDTO): Promise<UpdateRiskMatrixResponseDTO> {
		const url = this.basePath + `/risk_matrix/${params.id}`;
		const { data } = await this.getInstance().patch(url, params);
		return data;
	}

	public async createRiskMatrixActionPlan(
		params: CreateRiskMatrixActionPlanParamsDTO,
	): Promise<CreateRiskMatrixActionPlanResponseDTO> {
		const url = this.basePath + `/risk_matrix/action_plan`;
		const { data } = await this.getInstance().post(url, params);
		return data;
	}

	public async getRiskMatrixActionPlan(
		params: GetRiskMatrixActionPlanParamsDTO,
	): Promise<GetRiskMatrixActionPlanResponseDTO> {
		const url = this.basePath + `/risk_matrix/action_plan/${params.id}`;
		const { data } = await this.getInstance().get(url, { params });
		return data;
	}

	public async createRiskMatrixFileUrl(
		params: CreateRiskMatrixFileUrlParamsDTO,
	): Promise<CreateRiskMatrixFileUrlResponseDTO> {
		const url = this.basePath + '/risk_matrix/upload';
		const { data } = await this.getInstance().post(url, params);
		return data;
	}

	public async getRiskMatrixAttachments(
		params: GetRiskMatrixAttachmentsParamsDTO,
	): Promise<GetRiskMatrixAttachmentsResponseDTO[]> {
		const url = this.basePath + `/risk_matrix/attachments/${params.id}`;
		const { data } = await this.getInstance().get(url, { params });
		return data;
	}

	public async getRiskMatrixActionPlanAttachments(
		params: GetRiskMatrixActionPlanAttachmentsParamsDTO,
	): Promise<GetRiskMatrixActionPlanAttachmentsResponseDTO[]> {
		const url = this.basePath + `/risk_matrix/attachments/action_plan/${params.id}`;
		const { data } = await this.getInstance().get(url, { params });
		return data;
	}

	public async createRiskMatrixAttachment(
		params: CreateRiskMatrixAttachmentParamsDTO,
	): Promise<CreateRiskMatrixAttachmentResponseDTO[]> {
		const url = this.basePath + '/risk_matrix/attachments';
		const { data } = await this.getInstance().post(url, params);
		return data;
	}

	public async updateRiskMatrixActionPlan(
		params: UpdateRiskMatrixActionPlanParamsDTO,
	): Promise<UpdateRiskMatrixActionPlanResponseDTO> {
		const url = this.basePath + `/risk_matrix/action_plan/${params.id}`;
		const { data } = await this.getInstance().put(url, params);
		return data;
	}

	public async deleteRiskMatrixAttachment(
		params: DeleteRiskMatrixAttachmentParamsDTO,
	): Promise<DeleteRiskMatrixAttachmentResponseDTO> {
		const url = this.basePath + `/risk_matrix/attachments/${params.id}`;
		const { data } = await this.getInstance().delete(url, { params });
		return data;
	}

	public async updateRiskMatrixAttachment(
		params: UpdateRiskMatrixAttachmentParamsDTO,
	): Promise<UpdateRiskMatrixAttachmentResponseDTO> {
		const url = this.basePath + `/risk_matrix/attachments/${params.id}`;
		const { data } = await this.getInstance().put(url, params);
		return data;
	}

	public async createRiskMatrixFileInS3(params: CreateRiskMatrixFileInS3ParamsDTO) {
		return axios.put(params.url, params.file, {
			headers: {
				'Content-Type': params.file.type,
			},
		});
	}

	public async getEmployeeEducationsList(params: GetEmployeeEducationListRequestDTO): Promise<EmployeeEducation[]> {
		const url = this.basePath + '/employees/educations';
		const { data } = await this.getInstance().get<EmployeeEducation[]>(url, { params });
		return data;
	}

	public async createEmployeeEducation(
		params: CreateEmployeeEducationRequestDTO,
	): Promise<CreateEmployeeEducationResponseDTO> {
		const url = this.basePath + '/employee/education';
		const { data } = await this.getInstance().post<CreateEmployeeEducationResponseDTO>(url, params);
		return data;
	}

	public async getEmployeeWorkShiftsList(params: GetEmployeeWorkShiftListRequestDTO): Promise<EmployeeWorkShift[]> {
		const url = this.basePath + '/employees/workshifts';
		const { data } = await this.getInstance().get<EmployeeWorkShift[]>(url, { params });
		return data;
	}

	public async createEmployeeWorkshift(
		params: CreateEmployeeWorkShiftRequestDTO,
	): Promise<CreateEmployeeWorkShiftResponseDTO> {
		const url = this.basePath + '/employee/workshift';
		const { data } = await this.getInstance().post<CreateEmployeeWorkShiftResponseDTO>(url, params);
		return data;
	}
}
